import { announce } from '@react-aria/live-announcer';
import { ReactElement, useContext } from 'react';
import { useIntl } from 'react-intl';

import { AuthModalContext } from '@/context/AuthModalContextProvider';
import { useProgramFavorite } from '@/hooks/app/useProgramFavorite';
import { useUserState } from '@/hooks/store';
import heartIconFilled from 'icons/heart-filled.svg';
import heartIconOutline from 'icons/heart-outline.svg';

import messages from './messages';
import { Wrapper } from './styles';
import { ProgramFaveHeartProps } from './types';

const ProgramFaveHeart = ({ programId, programTitle }: ProgramFaveHeartProps): ReactElement => {
	const { formatMessage } = useIntl();
	const user = useUserState();
	const { setIsModalOpen } = useContext(AuthModalContext);
	const { isFaved, toggleFavorite } = useProgramFavorite(programId);

	const onPress = async (): Promise<void> => {
		// Shown to signed-out visitors — neither platform hides the heart on authentication
		// state — so the tap has to go somewhere. Android turns it into a login prompt, and the
		// write would only 401 anyway, so prompt rather than let it fail silently.
		if (!user) {
			setIsModalOpen(true);
			return;
		}

		const messageToAnnounce = isFaved ? messages.isUnfavedAnnouncement : messages.isFavedAnnouncement;
		announce(formatMessage(messageToAnnounce, { title: programTitle }));

		await toggleFavorite();
	};

	return (
		<Wrapper
			data-testid="program-fave-heart"
			aria-label={formatMessage(isFaved ? messages.unfaveAriaLabel : messages.faveAriaLabel)}
			aria-pressed={isFaved}
			onPress={onPress}
			Icon={isFaved ? heartIconFilled : heartIconOutline}
		/>
	);
};

export default ProgramFaveHeart;
